import { type Offer, OfferStatus, type Review } from '@nutralab/api';
import type { Brand } from '@nutralab/ui/brand';

import { getImageUrl } from './getImageUrl';
import { getOfferPath } from './getOfferPath';
import { toPlainText } from './toPlainText';

const SCHEMA_CONTEXT = 'https://schema.org';
const MAX_DESCRIPTION_LENGTH = 5000;

// Partial: a status the backend adds before we do falls back to "in stock"; `satisfies` keeps the list complete.
const AVAILABILITY: Partial<Record<OfferStatus, string>> = {
    [OfferStatus.ACTIVE]: 'https://schema.org/InStock',
    [OfferStatus.ENDING]: 'https://schema.org/LimitedAvailability',
    [OfferStatus.OUT_OF_STOCK]: 'https://schema.org/OutOfStock',
} satisfies Record<OfferStatus, string>;

interface BuildOfferJsonLdParams {
    offer: Offer;
    brand: Brand;
    reviews: Review[];
    origin: string;
    homeLabel: string;
}

export function buildOfferJsonLd({ offer, brand, reviews, origin, homeLabel }: BuildOfferJsonLdParams): object {
    const url = `${origin}${getOfferPath(offer)}`;
    const images = (offer.images.length > 0 ? offer.images : [offer.main_image]).map(
        (image) => new URL(getImageUrl(image, { shouldUseOriginal: true }), origin).href,
    );
    const description = toPlainText(offer.description, MAX_DESCRIPTION_LENGTH);

    const product: Record<string, unknown> = {
        '@type': 'Product',
        '@id': `${url}#product`,
        name: offer.name,
        url,
        image: images,
        sku: offer.sku,
        category: offer.category.name,
        brand: { '@type': 'Brand', name: brand.name },
        offers: {
            '@type': 'Offer',
            url,
            price: offer.price.amount,
            priceCurrency: offer.price.currency,
            availability: AVAILABILITY[offer.status] ?? AVAILABILITY[OfferStatus.ACTIVE],
        },
    };

    if (description) {
        product.description = description;
    }

    if (offer.rating.count > 0 && offer.rating.average !== null) {
        product.aggregateRating = {
            '@type': 'AggregateRating',
            ratingValue: offer.rating.average.toFixed(1),
            reviewCount: offer.rating.count,
            bestRating: 5,
            worstRating: 1,
        };
    }

    const reviewNodes = reviews
        .map((review) => ({ ...review, body: toPlainText(review.text) }))
        .filter((review) => review.body.length > 0)
        .map((review) => ({
            '@type': 'Review',
            author: { '@type': 'Person', name: toPlainText(review.public_name) },
            datePublished: review.created_at,
            reviewBody: review.body,
            reviewRating: {
                '@type': 'Rating',
                ratingValue: review.rating,
                bestRating: 5,
                worstRating: 1,
            },
        }));

    if (reviewNodes.length > 0) {
        product.review = reviewNodes;
    }

    const graph: object[] = [
        product,
        {
            '@type': 'BreadcrumbList',
            '@id': `${url}#breadcrumbs`,
            itemListElement: [
                { '@type': 'ListItem', position: 1, name: homeLabel, item: origin },
                { '@type': 'ListItem', position: 2, name: offer.name, item: url },
            ],
        },
    ];

    if (offer.qna.length > 0) {
        graph.push({
            '@type': 'FAQPage',
            '@id': `${url}#faq`,
            mainEntity: offer.qna.map((item) => ({
                '@type': 'Question',
                name: toPlainText(item.question),
                acceptedAnswer: { '@type': 'Answer', text: toPlainText(item.answer) },
            })),
        });
    }

    return { '@context': SCHEMA_CONTEXT, '@graph': graph };
}
